import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Row, Col } from "react-bootstrap";
import orderBy from "lodash/orderBy";
import Formsy from "formsy-react";
import { Input } from "formsy-react-components";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";

import DropdownSearch from "../../common/DropdownSearch";

import ProjectService from "../../../services/projectService";

const ProjectNotificationsForm = ({
  notification: initialNotification,
  codes,
  adding,
  onAdd,
  onSave,
  onCancel,
  project
}) => {
  const [notification, setNotification] = useState({});
  const [consumed, setConsumed] = useState(0);

  useEffect(() => {
    setNotification(initialNotification);
  }, [initialNotification]);

  useEffect(() => {
    const fetchConsumed = async () => {
      const type = codes.find(
        (code) =>
          code._id === notification.type?._id || code._id === notification?.type
      );

      if (type && type.value === "monthly_segment_budget_consumed") {
        const segment = project.budget_segments?.find(
          (x) =>
            new Date() >= startOfDay(new Date(x.startDate)) &&
            new Date() <= endOfDay(new Date(x.endDate))
        );
        if (segment) {
          const percentage = (segment.consumed / segment.budgetHours) * 100;
          setConsumed(percentage.toFixed(2));
        }
      } else if (type && type.value === "project_budget_consumed") {
        const result = await ProjectService.getBudgetConsumed(project._id);
        if (result && result[0]) {
          const percentage =
            (result[0].totalHours / project.budget_hours) * 100;
          setConsumed(percentage.toFixed(2));
        }
      }
    };
    fetchConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codes, project, notification]);

  const handleChange = (name, value) => {
    setNotification({ ...notification, [name]: value });
  };

  const generateCodeOptions = (entity, field) => {
    const codeOptions = codes
      .filter((code) => code.entity === entity && code.field === field)
      .filter(
        (code) =>
          project.budget_segments.length ||
          code.value !== "monthly_segment_budget_consumed"
      )
      .map((code) => ({ value: code._id, label: code.label }));

    return orderBy(codeOptions, "label");
  };

  const handleAdd = () => {
    onAdd({ ...notification, consumed });
  };

  const handeSave = () => {
    onSave({ ...notification, consumed });
  };

  const handleCancel = () => {
    onCancel();
  };

  const showConsumed = useMemo(() => {
    const type = codes.find(
      (code) =>
        code._id === notification.type?._id || code._id === notification?.type
    );
    return !!(
      type &&
      (type.value === "monthly_segment_budget_consumed" ||
        type.value === "project_budget_consumed")
    );
  }, [codes, notification]);

  return (
    <Formsy
      className="vertical form"
      onValidSubmit={adding ? handleAdd : handeSave}
    >
      <Row>
        <Col md={6}>
          <DropdownSearch
            required
            name="type"
            label="Type"
            onChange={(name, { value }) => handleChange(name, value)}
            value={notification.type?._id || notification.type || ""}
            data={[...generateCodeOptions("Project", "notification_type")]}
          />
        </Col>
        <Col md={6}>
          {!showConsumed ? (
            <Input
              required
              type="number"
              name="num_days"
              label="Number of Days Before"
              onChange={(name, value) => handleChange(name, parseFloat(value))}
              disabled={!notification.type}
              value={notification.num_days || ""}
              step="1"
              pattern="\d+"
              validations={{
                isInteger(values, value) {
                  return (
                    Number.isInteger(parseFloat(value)) &&
                    parseFloat(value) >= 0
                  );
                }
              }}
              validationErrors={{
                isInteger: "Please enter a positive integer"
              }}
            />
          ) : null}
          {showConsumed ? (
            <Input
              required
              type="number"
              name="consumed"
              label="Budget Consumed"
              disabled
              value={consumed}
            />
          ) : null}
        </Col>
        <Col md={12} xs={12} className="footer">
          <button
            type="button"
            className="btn  btn-cancel"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <input className="btn btn-success" type="submit" value="Save" />
        </Col>
      </Row>
    </Formsy>
  );
};

ProjectNotificationsForm.propTypes = {
  notification: PropTypes.object,
  codes: PropTypes.array,
  adding: PropTypes.bool,
  onAdd: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  project: PropTypes.object
};

ProjectNotificationsForm.defaultProps = {
  notification: {}
};

export default ProjectNotificationsForm;
